import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { doGetSteps } from '../../../store/course.slice';
import { doGetEvaluations, doCopyEvaluations, doUpdateEvaluation } from '../../../store/evaluation.slice';
import { doRemoveAuth } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';
import { useSnackbar } from 'notistack';
import { Button as AntButton, Modal, Select, Table, TableColumnsType } from 'antd';

import Loader from '../../../components/Loader';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Button from '../../../components/Button';
import Radio from '../../../components/Radio';
import { errorVariant, infoVariant, successVariant, warningVariant } from '../../../lib/barVariant';
import useAuth from '../../../hooks/useAuth';

const ProgramStep: FC = () => {
  const { writeAuthYn } = useAuth();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const dispatch = useDispatch();
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // GR0001 = 평가 구분
  const GR0001 = commonCodes.find(item => item.commonCode === 'GR0001')?.detailCode;

  const { steps, stepLoading } = useSelector((state: RootState) => state.course);
  const { evaluations, isLoading } = useSelector((state: RootState) => state.evaluation);
  const [courseData, setCourseData] = useState<any>([]);
  // 선택 중인 코스 정보
  const [courseCode, setCourseCode] = useState<number|null>(null);
  const [courseName, setCourseName] = useState('');
  const [levelName, setLevelName] = useState('');

  const [copyCourseVisible, setCopyCourseVisible] = useState(false);
  const [level, setLevel] = useState<any>();
  const [levelList, setLevelList] = useState<any>();
  const [step, setStep] = useState<any>();
  const [stepList, setStepList] = useState<any>();
  const [isStepDisabled, setIsStepDisabled] = useState(true);

  const [editCourseVisible, setEditCourseVisible] = useState(false);
  const [courseRecord, setCourseRecord] = useState<any>();
  const [useYn, setUseYn] = useState('Y');
  
  useEffect(() => {
    if (steps) {
      let data: any = [];
      steps.forEach(level => {
        const levelName = level.courseName;
        level.step?.forEach(step => {
          data.push({
            levelName,
            stepName: step.courseName,
            courseCode: step.courseCode,
          });
        })
      });
      setCourseData(data);

      data = [];
      steps.forEach(level => {
        data.push({
          label: level.courseName,
          value: level.courseCode
        });
      });
      setLevelList(data);
    }
  }, [steps]);

  useEffect(() => {
    getSteps();
  }, []);

  const getSteps = async () => {
    const res = await dispatch(doGetSteps());

    switch (res?.status) {
      case 200:
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }
  }

  const getEvaluations = async (courseCode: number) => {
    const res = await dispatch(doGetEvaluations(courseCode));

    switch (res?.status) {
      case 200:
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }
  }

  const onSetLevel = (value: string) => {
    const level = steps?.find(item => item.courseCode === value);
    if (level) {
      const data: any = [];
      level.step?.forEach(item => {
        if (Number(item.courseCode) !== courseCode) {
          data.push({
            label: item.courseName,
            value: item.courseCode
          });
        }
      });
      setStepList(data);
      setIsStepDisabled(false);
    }

    setStep(null);
    setLevel(value);
  }

  const onCloseCopyModal = () => {
    setLevel(false);
    setStep(false);
    setCopyCourseVisible(false);
  }

  const onCopyEvaluation = async () => {
    if (!courseCode) return;
    if (!step) {
      return enqueueSnackbar(t('program.step.15'), infoVariant);
    }

    const res = await dispatch(doCopyEvaluations(courseCode, step));

    switch (res?.status) {
      case 200:
        getEvaluations(courseCode);
        onCloseCopyModal();
        enqueueSnackbar(t('program.step.16'), successVariant);
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }
  }

  const onEditEvaluation = async () => {
    if (!courseCode) return;

    const res = await dispatch(doUpdateEvaluation(courseRecord.courseEvaluationCode, { useYn }));

    switch (res?.status) {
      case 200:
        getEvaluations(courseCode);
        setEditCourseVisible(false);
        enqueueSnackbar(t('common.message.edit1'), successVariant);
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }
  }

  const columns: TableColumnsType<any> = [
    {
      title: t('program.step.3'),
      dataIndex: 'brnCode',
      key: 'brnCode',
      sorter: (a, b) => a.brnCode?.localeCompare(b.brnCode),
      render: (value: string) => {
        const type = GR0001?.find(item => item.detailCode === value)
        return type?.korName || value;
      }
    },
    {
      title: t('program.step.4'),
      dataIndex: 'firstItemName',
      key: 'firstItemName',
      sorter: (a, b) => a.firstItemName?.localeCompare(b.firstItemName)
    },
    {
      title: t('program.step.5'),
      dataIndex: 'secondItemName',
      key: 'secondItemName',
      sorter: (a, b) => a.secondItemName?.localeCompare(b.secondItemName)
    },
    {
      title: 'Activity',
      key: 'thirdItemName',
      dataIndex: 'thirdItemName',
      sorter: (a, b) => a.thirdItemName?.localeCompare(b.thirdItemName)
    },
    {
      title: t('program.step.26'),
      key: 'useYn',
      dataIndex: 'useYn',
      sorter: (a, b) => a.useYn?.localeCompare(b.useYn)
    },
    {
      title: t('program.step.27'),
      key: 'typeName',
      dataIndex: 'typeName',
      sorter: (a, b) => a.typeName?.localeCompare(b.typeName)
    },
  ];

  return (
    <div className="content-wrapper">
      {stepLoading || isLoading ? <Loader /> : null}
      <Header
        parentMenu={t('common.menu.program')}
        currentMenu={t('program.step.17')}
        iconFile="icon-program-big.svg"
      />
      <div className="main flex">
        {/* left side */}
        <div className="col-4">
          <div>
            <div className="card">
              <div className="card-header">
                {t('program.step.18')}
              </div>
              <div className="card-body">
                <table className="table table-bordered table-basic">
                  <colgroup>
                    <col width="50%" />
                    <col width="50%" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="text-center">{t('program.step.8')}</th>
                      <th className="text-center">{t('program.step.9')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {courseData.map((item: any, index: number) => (
                      <tr
                        key={item.courseCode}
                        className={courseCode === item.courseCode ? ' chosen' : 'pointer'}
                        onClick={() => {
                          setCourseCode(item.courseCode);
                          setCourseName(item.stepName);
                          setLevelName(item.levelName);
                          getEvaluations(item.courseCode);
                        }}
                      >
                        <td>{item.levelName}</td>
                        <td>{item.stepName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* right side */}
        {
          courseCode
            ? (
              <div className="col-8">
                <div>
                  <div className="card">
                    <div className="card-header flex flex-justify-between py-2">
                      <div className="w-50" style={{ transform: 'translateY(4px)' }}>{t('program.step.19')}</div>
                      <div className="w-50 text-right">
                        <Button
                          className="btn btn-success btn-w100p mr-1"
                          onClick={() => setCopyCourseVisible(true)}
                          text={t('program.step.20')}
                          disabled={evaluations.length > 0}
                          writeAuthYn={writeAuthYn}
                        />
                        <Button
                          className="btn btn-primary btn-w100p"
                          onClick={() => history.push(`/program/step/${courseCode}/create?level=${levelName}&step=${courseName}`)}
                          text={t('program.step.21')}
                          writeAuthYn={writeAuthYn}
                        />
                      </div>
                    </div>
                    <div className="card-body pb-0">
                      <Table
                        columns={columns}
                        dataSource={evaluations || undefined}
                        pagination={{
                          pageSize: 99999
                        }}
                        onRow={record => {
                          return {
                            onClick: () => {
                              setCourseRecord(record);
                              setUseYn(record.useYn);
                              setEditCourseVisible(true);
                            }
                          };
                        }}
                        rowClassName="pointer"
                      />
                    </div>
                    <div className="card-footer text-right pt-0">
                      <Button
                        className="btn btn-success btn-w100p mr-1"
                        onClick={() => setCopyCourseVisible(true)}
                        text={t('program.step.20')}
                        disabled={evaluations.length > 0}
                        writeAuthYn={writeAuthYn}
                      />
                      <Button
                        className="btn btn-primary btn-w100p"
                        onClick={() => history.push(`/program/step/${courseCode}/create?level=${levelName}&step=${courseName}`)}
                        text={t('program.step.21')}
                        writeAuthYn={writeAuthYn}
                      />
                    </div>
                  </div>
                </div>
              </div>
              )
            : null
        }
      </div>
      <Footer />

      {/* 평가항목 복사 모달 */}
      <Modal
        title={`${t('program.step.22')} - ${courseName}`}
        visible={copyCourseVisible}
        onCancel={onCloseCopyModal}
        footer={[
          <div className='comm-btn__area justify-content-center'>
            <AntButton
              key="submit"
              className='btn-basic pink'
              onClick={onCopyEvaluation}
            >
              {t('program.step.20')}
            </AntButton>
            <AntButton
              key="cancel"
              className='btn-basic gray'
              onClick={onCloseCopyModal}
            >
              {t('common.button.cancel')}
            </AntButton>
          </div>
        ]}
      >
        <div className="flex flex-justify-center">
          <div className="w-210p mr-1">
            <Select
              className="w-100"
              placeholder={t('program.step.23')}
              value={level}
              onChange={value => onSetLevel(value)}
            >
              {levelList?.map((item: any) => (
                <Select.Option
                  key={item.value}
                  value={item.value}
                >
                  {item.label}
                </Select.Option>
              ))}
            </Select>            
          </div>
          <div className="w-210p">
            <Select
              className="w-100"
              placeholder={t('program.step.24')}
              value={step}
              onChange={value => setStep(value)}
              disabled={isStepDisabled}
            >
              {stepList?.map((item: any) => (
                <Select.Option
                  key={item.value}
                  value={item.value}
                >
                  {item.label}
                </Select.Option>
              ))}
            </Select>            
          </div>
        </div>
      </Modal>

      {/* 평가항목 수정 모달 */}
      <Modal
        title={t('program.step.25')}
        visible={editCourseVisible}
        onCancel={() => setEditCourseVisible(false)}
        footer={[
          <div className='comm-btn__area justify-content-center'>
            <AntButton
              key="submit"
              className='btn-basic pink'
              onClick={onEditEvaluation}
            >
              {t('common.button.save')}
            </AntButton>
            <AntButton
              key="cancel"
              className='btn-basic gray'
              onClick={() => setEditCourseVisible(false)}
            >
              {t('common.button.cancel')}
            </AntButton>
          </div>
        ]}
      >
        <div>
          <table className="table table-bordered table-basic w-100">
            <colgroup>
              <col width="30%" />
              <col width="70%" />
            </colgroup>
            <tbody>
              <tr>
                <th>{t('program.step.3')}</th>
                <td>{(() => {
                  const type = GR0001?.find(item => item.detailCode === courseRecord?.brnCode)
                  return type?.korName || courseRecord?.brnCode;
                })()}</td>
              </tr>
              <tr>
                <th>{t('program.step.4')}</th>
                <td>{courseRecord?.firstItemName}</td>
              </tr>
              <tr>
                <th>{t('program.step.5')}</th>
                <td>{courseRecord?.secondItemName}</td>
              </tr>
              <tr>
                <th>Activity</th>
                <td>{courseRecord?.thirdItemName}</td>
              </tr>
              <tr>
                <th>
                  {t('program.step.26')}
                </th>
                <td className="flex">
                  <Radio
                    checked={useYn === 'Y'}
                    onChange={() => setUseYn('Y')}
                    text={t('common.option.yes')}
                  />
                  <div className="mr-4" />
                  <Radio
                    checked={useYn === 'N'}
                    onChange={() => setUseYn('N')}
                    text={t('common.option.no')}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Modal>
    </div>
  )
}

export default ProgramStep;
